import { z } from 'zod';
import { IsoDateTime, Locale, Uuid } from './common.js';
/**
 * Groups module (docs/modules/groups-members.md). Owner: Phase 1 task S1.
 */
export const GroupKind = z.enum(['standard', 'repeat']);
export const GroupStatus = z.enum(['active', 'archived']);
export const Group = z.object({
    id: Uuid,
    name: z.string(),
    kind: GroupKind,
    /** Kiosk and portal order (drag and drop). */
    sortOrder: z.number().int(),
    status: GroupStatus,
    defaultLanguage: Locale,
    /** True when a 4-digit kiosk PIN is set for opening this group. The PIN itself is never returned. */
    hasKioskPin: z.boolean(),
    /** Repeat groups only: current (not archived) members. Null for standard groups. */
    memberCount: z.number().int().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const GroupCreate = z.object({
    name: z.string().trim().min(1).max(60),
    kind: GroupKind,
    defaultLanguage: Locale.optional(),
});
/** `kind` can't change after creation. */
export const GroupUpdate = z.object({
    name: z.string().trim().min(1).max(60).optional(),
    defaultLanguage: Locale.optional(),
    status: GroupStatus.optional(),
});
export const GroupListQuery = z.object({
    status: z.enum(['active', 'archived', 'all']).default('active'),
    /** Only groups visible on this site. */
    siteId: Uuid.optional(),
});
/** PUT /groups/order: every active group id, in the new order. */
export const GroupOrder = z.object({ groupIds: z.array(Uuid).min(1).max(200) });
/** PUT /groups/:id/kiosk-pin. null removes the PIN. */
export const KioskPinSet = z.object({ pin: z.string().regex(/^\d{4}$/).nullable() });
/** Group-level settings of the `groups` module (scoped settings, ADR 0013). */
export const GroupsGroupSettings = z.object({
    kiosk: z
        .object({
        showOnKiosk: z.boolean().default(true),
        /** Repeat groups: people not on the list can add themselves at the kiosk (creates a member). */
        allowNewMembersFromKiosk: z.boolean().default(false),
        /** Repeat groups: members can retake their profile photo at the kiosk. */
        allowMembersUpdatePhoto: z.boolean().default(false),
        /** How names show in kiosk lists and autocomplete. */
        nameDisplay: z.enum(['full', 'first_name_initial']).default('full'),
    })
        .default({ showOnKiosk: true, allowNewMembersFromKiosk: false, allowMembersUpdatePhoto: false, nameDisplay: 'full' }),
    signIn: z
        .object({
        /** Shown on the kiosk thank-you screen after sign-in. Empty = i18n default. */
        welcomeMessage: z.string().max(500).default(''),
    })
        .default({ welcomeMessage: '' }),
    signOut: z
        .object({
        message: z.string().max(500).default(''),
    })
        .default({ message: '' }),
});
export const GroupSummary = z.object({ id: Uuid, name: z.string(), kind: GroupKind, sortOrder: z.number().int() });
//# sourceMappingURL=groups.js.map